'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';

const SDKIntegration = () => {
  const iframeRef = useRef(null);

  const [config, setConfig] = useState({
    public_key: 'merchant_public_key',
    secret_key: 'merchant_secret_key',
    email: 'company.mayur2@yopmail.com',
    name: '',
    designation: '',
    primaryColor: '#FB512D',
    secondaryColor: '#004469',
    redirectUrl: '',
  });

  const [sdkUrl, setSdkUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sdkStatus, setSdkStatus] = useState('Ready');
  const [events, setEvents] = useState([]);
  const [error, setError] = useState('');

  // Add logs
  const addEventLog = useCallback((eventName, data) => {
    const timestamp = new Date().toLocaleTimeString();

    setEvents((prev) => [
      {
        timestamp,
        eventName,
        data,
      },
      ...prev,
    ]);
  }, []);

  // Generate SDK URL API
  const generateSDKLink = async () => {
    try {
      setIsLoading(true);
      setError('');
      setSdkStatus('Generating SDK Link...');

      const response = await fetch(
        'http://localhost:8000/apis/sdk/generate-sdk-link/',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            public_key: config.public_key,
            secret_key: config.secret_key,
            timestamp: Math.floor(Date.now() / 1000),
            email: config.email,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || data.statusCode !== 200) {
        throw new Error(data?.message || 'Failed to generate SDK URL');
      }

      let finalUrl = data.sdk_url;

      // Add optional params
      const url = new URL(finalUrl);

      if (config.name) {
        url.searchParams.set('name', config.name);
      }

      if (config.designation) {
        url.searchParams.set('designation', config.designation);
      }

      if (config.primaryColor) {
        url.searchParams.set('primaryColor', config.primaryColor);
      }

      if (config.secondaryColor) {
        url.searchParams.set('secondaryColor', config.secondaryColor);
      }

      if (config.redirectUrl) {
        url.searchParams.set('redirect_url', config.redirectUrl);
      }

      finalUrl = url.toString();

      setSdkUrl(finalUrl);
      setSdkStatus('SDK Loaded');

      addEventLog('sdk_generated', finalUrl);
    } catch (err) {
      console.error(err);

      setError(err.message || 'Something went wrong');
      setSdkStatus('Error');

      addEventLog('sdk_error', err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle iframe events
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'trevex_event') {
        const { eventName, data } = event.data;

        addEventLog(eventName, data);

        switch (eventName) {
          case 'ready':
            setSdkStatus('Ready');
            break;

          case 'flow_completed':
            setSdkStatus('Completed');
            break;

          case 'error':
            setSdkStatus('Error');
            break;

          default:
            break;
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [addEventLog]);

  // Handle input
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setConfig((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Reset SDK
  const resetSDK = () => {
    setSdkUrl('');
    setSdkStatus('Ready');
    setError('');
    setEvents([]);

    addEventLog('sdk_reset', 'SDK reset successfully');
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900">
            TREVEX SDK Integration
          </h1>

          <p className="text-gray-500 mt-2">
            Secure SDK Authentication & Integration Testing
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Left Panel */}
          <div className="lg:col-span-1 space-y-6">

            {/* Configuration */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">

              <h2 className="text-xl font-semibold mb-5 text-gray-800">
                SDK Configuration
              </h2>

              <div className="space-y-4">

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Public Key
                  </label>

                  <input
                    type="text"
                    name="public_key"
                    value={config.public_key}
                    onChange={handleInputChange}
                    className="w-full h-11 px-4 rounded-xl border border-gray-300 focus:ring-2 focus:ring-orange-500 outline-none"
                    placeholder="Merchant Public Key"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Secret Key
                  </label>

                  <input
                    type="password"
                    name="secret_key"
                    value={config.secret_key}
                    onChange={handleInputChange}
                    className="w-full h-11 px-4 rounded-xl border border-gray-300 focus:ring-2 focus:ring-orange-500 outline-none"
                    placeholder="Merchant Secret Key"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>

                  <input
                    type="email"
                    name="email"
                    value={config.email}
                    onChange={handleInputChange}
                    className="w-full h-11 px-4 rounded-xl border border-gray-300 focus:ring-2 focus:ring-orange-500 outline-none"
                    placeholder="Enter Email"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name
                  </label>

                  <input
                    type="text"
                    name="name"
                    value={config.name}
                    onChange={handleInputChange}
                    className="w-full h-11 px-4 rounded-xl border border-gray-300 focus:ring-2 focus:ring-orange-500 outline-none"
                    placeholder="Full Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Designation
                  </label>

                  <input
                    type="text"
                    name="designation"
                    value={config.designation}
                    onChange={handleInputChange}
                    className="w-full h-11 px-4 rounded-xl border border-gray-300 focus:ring-2 focus:ring-orange-500 outline-none"
                    placeholder="Designation"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Primary Color
                    </label>

                    <input
                      type="color"
                      name="primaryColor"
                      value={config.primaryColor}
                      onChange={handleInputChange}
                      className="w-full h-11 rounded-xl border border-gray-300 cursor-pointer"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Secondary Color
                    </label>

                    <input
                      type="color"
                      name="secondaryColor"
                      value={config.secondaryColor}
                      onChange={handleInputChange}
                      className="w-full h-11 rounded-xl border border-gray-300 cursor-pointer"
                    />
                  </div>

                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Redirect URL
                  </label>

                  <input
                    type="text"
                    name="redirectUrl"
                    value={config.redirectUrl}
                    onChange={handleInputChange}
                    className="w-full h-11 px-4 rounded-xl border border-gray-300 focus:ring-2 focus:ring-orange-500 outline-none"
                    placeholder="https://merchant.com/success"
                  />
                </div>

              </div>

              {/* Buttons */}
              <div className="flex gap-3 mt-6">

                <button
                  onClick={generateSDKLink}
                  disabled={isLoading}
                  className="flex-1 h-12 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold transition-all disabled:opacity-50"
                >
                  {isLoading ? 'Generating...' : 'Generate & Load SDK'}
                </button>

                <button
                  onClick={resetSDK}
                  className="h-12 px-5 rounded-xl border border-gray-300 hover:bg-gray-100 font-medium transition-all"
                >
                  Reset
                </button>

              </div>

              {/* Error */}
              {error && (
                <div className="mt-4 p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                  {error}
                </div>
              )}

            </div>

            {/* Status */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">

              <h2 className="text-xl font-semibold mb-4 text-gray-800">
                SDK Status
              </h2>

              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-3 rounded-full ${
                    sdkStatus === 'Completed'
                      ? 'bg-green-500'
                      : sdkStatus === 'Error'
                      ? 'bg-red-500'
                      : sdkStatus.includes('Loading')
                      ? 'bg-yellow-500'
                      : 'bg-blue-500'
                  }`}
                />

                <span className="font-medium text-gray-700">
                  {sdkStatus}
                </span>
              </div>

            </div>

          </div>

          {/* Right Panel */}
          <div className="lg:col-span-2 space-y-6">

            {/* SDK Preview */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">

              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-800">
                  SDK Preview
                </h2>

                {sdkUrl && (
                  <a
                    href={sdkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-orange-500 hover:underline"
                  >
                    Open in New Tab
                  </a>
                )}
              </div>

              <div className="h-[700px] bg-gray-50">

                {sdkUrl ? (
                  <iframe
                    ref={iframeRef}
                    src={sdkUrl}
                    title="TREVEX SDK"
                    className="w-full h-full border-0"
                    onLoad={() => {
                      setSdkStatus('Loaded');
                      addEventLog('iframe_loaded', 'SDK iframe loaded');
                    }}
                  />
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400">
                    SDK Preview will appear here
                  </div>
                )}

              </div>

            </div>

            {/* Event Logs */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200">

              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">

                <h2 className="text-xl font-semibold text-gray-800">
                  Event Logs
                </h2>

                <button
                  onClick={() => setEvents([])}
                  className="text-sm text-red-500 hover:underline"
                >
                  Clear Logs
                </button>

              </div>

              <div className="max-h-[300px] overflow-auto">

                {events.length === 0 ? (
                  <div className="p-6 text-gray-400 text-sm">
                    No events yet
                  </div>
                ) : (
                  events.map((event, index) => (
                    <div
                      key={index}
                      className="px-6 py-3 border-b border-gray-100 text-sm"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-gray-800">
                          {event.eventName}
                        </span>

                        <span className="text-gray-400 text-xs">
                          {event.timestamp}
                        </span>
                      </div>

                      <div className="text-gray-500 mt-1 break-all">
                        {typeof event.data === 'string'
                          ? event.data
                          : JSON.stringify(event.data)}
                      </div>
                    </div>
                  ))
                )}

              </div>

            </div>

          </div>

        </div>
      </div>
    </div>
  );
};

export default SDKIntegration;